import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Eye, EyeOff, Check, X, Copy, Download, Shield, Gift } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import api from "@/lib/api";
import { getStoredReferralCode, clearStoredReferralCode } from "@/pages/ReferralCapturePage";

export default function ProSetupModal({ open, onComplete }) {
  const [step, setStep] = useState("credentials"); // "credentials" | "recovery"
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "unavailable" | "invalid"
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesConfirmed, setCodesConfirmed] = useState(false);
  const debounceRef = useRef(null);

  // Detect a referral code stored from /r/{code} or ?ref= so we can show the
  // "invite bonus detected" notice. Computed once per mount because the cookie
  // /localStorage value is set well before this modal opens.
  const detectedReferralCode = getStoredReferralCode();

  // Username validation (debounced)
  const checkUsername = useCallback(async (val) => {
    if (!val || val.length < 3) {
      setUsernameStatus(val.length > 0 ? "invalid" : null);
      return;
    }
    setUsernameStatus("checking");
    try {
      const { data } = await api.post("/pro/check-username", { username: val });
      setUsernameStatus(data.available ? "available" : "unavailable");
    } catch {
      setUsernameStatus("available"); // allow attempt on network error
    }
  }, []);

  const handleUsernameChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9._-]/g, "");
    setUsername(clean);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!clean) { setUsernameStatus(null); return; }
    debounceRef.current = setTimeout(() => checkUsername(clean), 400);
  };

  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSetup = async () => {
    if (!passwordValid || !passwordsMatch || usernameStatus !== "available") return;
    setLoading(true);
    try {
      // Pass any stored referral code (from /r/{code} or ?ref=CODE) so the
      // backend can attach the referral immediately at account creation.
      const referralCode = getStoredReferralCode();
      const { data } = await api.post("/pro/setup-account", {
        username: username.trim(),
        password,
        password_confirm: confirmPassword,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      // Successful capture path → clear the stored code so we don't re-attach
      // for the next account on the same browser.
      if (referralCode) {
        try { clearStoredReferralCode(); } catch { /* ignore */ }
      }
      setRecoveryCodes(data.recovery_codes);
      setStep("recovery");
      toast.success("Account created");
    } catch (err) {
      toast.error(getErrorMessage(err, "Setup failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied to clipboard");
  };

  const handleDownloadCodes = () => {
    const text = [
      "QuickInbox PRO — Recovery Codes",
      `Username: ${username}`,
      `Generated: ${new Date().toISOString()}`,
      "",
      "Each code can only be used ONCE.",
      "Store these codes in a safe place.",
      "",
      ...recoveryCodes.map((c, i) => `${i + 1}. ${c}`),
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quickinbox-recovery-codes-${username}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recovery codes downloaded");
  };

  const handleFinish = () => {
    if (!codesConfirmed) {
      toast.error("Please confirm you've saved your recovery codes");
      return;
    }
    onComplete?.();
  };

  return (
    <Dialog open={open} onOpenChange={() => {/* Not closable */}}>
      <DialogContent
        className="sm:max-w-[460px] p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="pro-setup-modal"
      >
        <div className="max-h-[85vh] overflow-y-auto p-6">
        {step === "credentials" ? (
          <>
            <DialogHeader>
              <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center mb-2">
                <Shield size={20} className="text-white" />
              </div>
              <DialogTitle className="text-lg font-semibold">Secure your PRO account</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Create login credentials to access your permanent inbox.
              </DialogDescription>
            </DialogHeader>

            {detectedReferralCode && (
              <div
                className="mt-3 flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200"
                data-testid="pro-setup-referral-banner"
              >
                <Gift size={14} className="text-amber-600 shrink-0" />
                <p className="flex-1 text-xs text-amber-800">
                  Invite bonus detected: you'll receive <span className="font-semibold">+1 day</span> after activation.
                </p>
                <code
                  className="text-[10px] font-mono font-medium text-amber-700 bg-white/60 rounded px-1.5 py-0.5 shrink-0"
                  data-testid="pro-setup-referral-code"
                >
                  {detectedReferralCode}
                </code>
              </div>
            )}

            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Username</Label>
                <Input
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="choose a username"
                  className="font-mono"
                  autoFocus
                  data-testid="pro-setup-username"
                />
                <div className="h-4 flex items-center gap-1.5">
                  {usernameStatus === "checking" && <Loader2 size={12} className="text-zinc-500 animate-spin" />}
                  {usernameStatus === "available" && <><Check size={12} className="text-green-500" /><span className="text-[11px] text-green-600">Available</span></>}
                  {usernameStatus === "unavailable" && <><X size={12} className="text-red-500" /><span className="text-[11px] text-red-500">This username is not available</span></>}
                  {usernameStatus === "invalid" && <><X size={12} className="text-zinc-500" /><span className="text-[11px] text-zinc-500">At least 3 characters</span></>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="pr-10"
                    data-testid="pro-setup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#888888]"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password.length > 0 && !passwordValid && (
                  <p className="text-[11px] text-red-500">Password must be at least 8 characters</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Confirm password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                  data-testid="pro-setup-confirm-password"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-[11px] text-red-500">Passwords do not match</p>
                )}
              </div>
            </div>

            <Button
              onClick={handleSetup}
              disabled={loading || usernameStatus !== "available" || !passwordValid || !passwordsMatch}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10 mt-2"
              data-testid="pro-setup-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Save your recovery codes</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                These codes are the <strong className="text-[#555555]">only way</strong> to recover your account if you forget your password. Each code can only be used once. Store them securely.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-4 my-2" data-testid="pro-setup-recovery-codes">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, i) => (
                  <div key={i} className="font-mono text-sm text-[#555555] bg-white rounded px-2.5 py-1.5 border border-[#E5E5E5] text-center tracking-wider">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={handleCopyCodes}
                data-testid="pro-setup-copy-codes"
              >
                <Copy size={13} className="mr-1.5" /> Copy codes
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={handleDownloadCodes}
                data-testid="pro-setup-download-codes"
              >
                <Download size={13} className="mr-1.5" /> Download
              </Button>
            </div>

            <div className="flex items-start gap-2.5 mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Checkbox
                id="codes-confirmed"
                checked={codesConfirmed}
                onCheckedChange={setCodesConfirmed}
                data-testid="pro-setup-confirm-checkbox"
              />
              <label htmlFor="codes-confirmed" className="text-xs text-amber-800 leading-relaxed cursor-pointer">
                I have saved my recovery codes in a secure location. I understand that without these codes, I cannot recover my account.
              </label>
            </div>

            <Button
              onClick={handleFinish}
              disabled={!codesConfirmed}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10 mt-2"
              data-testid="pro-setup-finish"
            >
              Continue to dashboard
            </Button>
          </>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
